/**
 * Decides whether the app boots into the mobile tree (`src/mobile`) or the
 * desktop tree (`src/pages` + `src/app/routers`).
 *
 * Resolution order, most explicit first:
 *   1. `?ui=mobile` / `?ui=desktop` in the URL — also persisted, so the choice
 *      survives navigation once the query string is gone.
 *   2. A previously persisted choice in localStorage.
 *   3. Device sniffing: a coarse pointer (finger, not mouse) on a narrow
 *      viewport, or a phone/tablet user-agent.
 *
 * Step 3 deliberately combines `pointer: coarse` with a width check rather than
 * trusting either alone: a touchscreen laptop reports a coarse pointer but
 * wants the desktop layout, and a narrow desktop window is a resized browser,
 * not a phone.
 */

export type UiMode = 'mobile' | 'desktop'

const UI_MODE_STORAGE_KEY = 'abetting.uiMode'
const UI_MODE_QUERY_PARAM = 'ui'
const MOBILE_MAX_WIDTH_PX = 900
const MOBILE_USER_AGENT_PATTERN =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i

const isUiMode = (value: string | null): value is UiMode =>
  value === 'mobile' || value === 'desktop'

const readQueryOverride = (): UiMode | null => {
  const requested = new URLSearchParams(window.location.search).get(
    UI_MODE_QUERY_PARAM,
  )
  if (!isUiMode(requested)) {
    return null
  }
  return requested
}

const readStoredMode = (): UiMode | null => {
  try {
    const stored = window.localStorage.getItem(UI_MODE_STORAGE_KEY)
    if (!isUiMode(stored)) {
      return null
    }
    return stored
  } catch {
    // Private browsing in an embedded webview can throw on localStorage access.
    return null
  }
}

const persistMode = (mode: UiMode) => {
  try {
    window.localStorage.setItem(UI_MODE_STORAGE_KEY, mode)
  } catch {
    // Persistence is a convenience, never a requirement — ignore failures.
  }
}

const detectModeFromDevice = (): UiMode => {
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  const isNarrowViewport = window.innerWidth <= MOBILE_MAX_WIDTH_PX
  const hasMobileUserAgent = MOBILE_USER_AGENT_PATTERN.test(
    window.navigator.userAgent,
  )

  if (hasMobileUserAgent) {
    return 'mobile'
  }
  if (hasCoarsePointer && isNarrowViewport) {
    return 'mobile'
  }
  return 'desktop'
}

export const resolveUiMode = (): UiMode => {
  if (typeof window === 'undefined') {
    return 'desktop'
  }

  const queryOverride = readQueryOverride()
  if (queryOverride) {
    persistMode(queryOverride)
    return queryOverride
  }

  const storedMode = readStoredMode()
  if (storedMode) {
    return storedMode
  }

  return detectModeFromDevice()
}

/**
 * Switches the app to the other tree. A full reload is intentional: the two
 * trees have separate theme providers and route tables, so swapping them in
 * place would leave stale providers mounted.
 */
export const switchUiMode = (mode: UiMode) => {
  persistMode(mode)
  window.location.reload()
}
